import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { deviceAPI, fiberAPI, oltAPI, settingsAPI, optionsAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Server, Plus, Search, Download, X, AlertCircle, Trash2, RefreshCw } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// ─── Add Device Modal ─────────────────────────────────────────────────────────
// OLT/splitter/port fields removed — auto-fetched from fiber mappings on serial input
function AddDeviceModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    serialNumber: '', model: '', deviceType: 'onu',
    notes: '', purchaseDate: '', purchasePrice: '', vendor: '',
    olt: { name: '', ip: '', ponPort: '' },
    splitter: { name: '', location: '', port: '' },
  });
  const [loading, setLoading]     = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected]   = useState(null);
  const [mappings, setMappings]   = useState([]);
  const [oltCfg, setOltCfg]       = useState(null);
  const [models, setModels]       = useState([]);
  const [vendors, setVendors]     = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    fiberAPI.getMappings().then(r => setMappings(r.data?.data || [])).catch(() => {});
    settingsAPI.getOLT().then(r => setOltCfg(r.data?.data || null)).catch(() => {});
    optionsAPI.get('device_model').then(r => setModels(r.data?.data || [])).catch(() => {});
    optionsAPI.get('device_vendor').then(r => setVendors(r.data?.data || [])).catch(() => {});
  }, []);

  const mappingIndex = {};
  for (const m of mappings) mappingIndex[m.onuSerial] = m;

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSerialChange = (val) => {
    const upper = val.toUpperCase();
    set('serialNumber', upper);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const s = upper.trim();
      if (s.length < 4) { setDetected(null); return; }
      setDetecting(true);
      const map = mappingIndex[s];
      if (map) {
        setDetected({
          ponPort: map.ponPort,
          splitter: map.splitterId?.name || null,
          splitterLoc: map.splitterId?.location || null,
          splitterPort: map.splitterPort,
          customer: map.customerId?.name || null,
        });
        setForm(f => ({
          ...f,
          olt: { name: oltCfg?.name || '', ip: oltCfg?.ip || '', ponPort: map.ponPort != null ? String(map.ponPort) : '' },
          splitter: { name: map.splitterId?.name || '', location: map.splitterId?.location || '', port: map.splitterPort != null ? String(map.splitterPort) : '' },
        }));
      } else {
        setDetected({ notFound: true });
        setForm(f => ({ ...f, olt: { name: oltCfg?.name || '', ip: oltCfg?.ip || '', ponPort: '' }, splitter: { name: '', location: '', port: '' } }));
      }
      setDetecting(false);
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await deviceAPI.create(form);
      toast.success('Device added to inventory');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add device');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Add Device to Inventory</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">

            {/* Serial — auto-looks up fiber mapping */}
            <div className="form-group">
              <label className="form-label">Serial Number <span className="required">*</span></label>
              <div style={{ position: 'relative' }}>
                <input className="form-control font-mono" value={form.serialNumber} required
                  onChange={e => handleSerialChange(e.target.value)}
                  placeholder="Enter serial — OLT/splitter info auto-fetches" />
                {detecting && (
                  <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                    <div className="spinner" style={{ width: 14, height: 14 }} />
                  </div>
                )}
              </div>
            </div>

            {/* Auto-detected topology */}
            {detected && !detected.notFound && (
              <div style={{ padding: '9px 12px', borderRadius: 6, marginBottom: 10, fontSize: '.81rem', background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-86efac)', display: 'flex', flexWrap: 'wrap', gap: '4px 14px', color: 'var(--c-065f46)' }}>
                {oltCfg?.name && <span><strong>OLT:</strong> {oltCfg.name}</span>}
                {detected.ponPort != null && <span><strong>PON Port:</strong> {detected.ponPort}</span>}
                {detected.splitter && (
                  <span>
                    <strong>Splitter:</strong> {detected.splitter}
                    {detected.splitterPort ? ` · P${detected.splitterPort}` : ''}
                    {detected.splitterLoc ? ` (${detected.splitterLoc})` : ''}
                  </span>
                )}
                {detected.customer && <span><strong>In use by:</strong> {detected.customer}</span>}
              </div>
            )}
            {detected?.notFound && (
              <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: '.81rem', background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', color: 'var(--c-6b7280)', display: 'flex', alignItems: 'center', gap: 7 }}>
                <AlertCircle size={13} /> Not found in fiber mapping — topology fields will be empty
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Device Type</label>
                <select className="form-control" value={form.deviceType} onChange={e => set('deviceType', e.target.value)}>
                  <option value="onu">ONU</option>
                  <option value="olt">OLT</option>
                  <option value="router">Router (Broadband)</option>
                  <option value="set_top_box">STB (CableTV)</option>
                  <option value="switch">Switch</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Model <span className="required">*</span></label>
                <select className="form-control" value={form.model} onChange={e => set('model', e.target.value)} required>
                  <option value="">— Select Model —</option>
                  {form.model && !models.includes(form.model) && (
                    <option value={form.model}>{form.model}</option>
                  )}
                  {models.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Vendor</label>
              <select className="form-control" value={form.vendor} onChange={e => set('vendor', e.target.value)}>
                <option value="">— Select Vendor —</option>
                {form.vendor && !vendors.includes(form.vendor) && (
                  <option value={form.vendor}>{form.vendor}</option>
                )}
                {vendors.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Purchase Date</label>
                <input type="date" className="form-control" value={form.purchaseDate} onChange={e => set('purchaseDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Purchase Price (₹)</label>
                <input type="number" className="form-control" value={form.purchasePrice} onChange={e => set('purchasePrice', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <Server size={14} /> {loading ? 'Adding…' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Sync from OLT Modal ──────────────────────────────────────────────────────
function SyncOLTModal({ onClose, onSuccess }) {
  const [step, setStep]             = useState('loading');
  const [onus, setOnus]             = useState([]);
  const [mappings, setMappings]     = useState([]);
  const [oltCfg, setOltCfg]         = useState(null);
  const [error, setError]           = useState(null);
  const [result, setResult]         = useState(null);
  const [selected, setSelected]     = useState(new Set());
  const [existingSerials, setExistingSerials] = useState(new Set());
  const [skipDups, setSkipDups]     = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [onuRes, mapRes, cfgRes, devRes] = await Promise.allSettled([
          oltAPI.getONUs(),
          fiberAPI.getMappings(),
          settingsAPI.getOLT(),
          deviceAPI.getAll({ limit: 1000, deviceType: 'onu' }),
        ]);

        const rawOnus = onuRes.status === 'fulfilled'
          ? (Array.isArray(onuRes.value.data) ? onuRes.value.data : onuRes.value.data?.data || [])
          : [];

        if (onuRes.status === 'rejected' || rawOnus.length === 0) {
          setError(onuRes.reason?.response?.data?.message || 'Cannot reach OLT — configure in Settings first');
          setStep('error');
          return;
        }

        const existSet = new Set(
          (devRes.status === 'fulfilled' ? (devRes.value.data?.data || []) : [])
            .map(d => d.serialNumber?.toUpperCase())
            .filter(Boolean)
        );

        setOnus(rawOnus);
        setMappings(mapRes.status === 'fulfilled' ? (mapRes.value.data?.data || []) : []);
        setOltCfg(cfgRes.status === 'fulfilled' ? (cfgRes.value.data?.data || null) : null);
        setExistingSerials(existSet);
        // default: skip duplicates → only select new ones
        setSelected(new Set(
          rawOnus.map((o, i) => ({ o, i }))
            .filter(({ o }) => !existSet.has((o.serialNumber || o.id || '').toUpperCase()))
            .map(({ i }) => i)
        ));
        setStep('preview');
      } catch (err) {
        setError(err.message || 'Load failed');
        setStep('error');
      }
    };
    load();
  }, []);

  const mappingIndex = {};
  for (const m of mappings) mappingIndex[m.onuSerial] = m;

  // Track which serials appear more than once in the OLT data itself
  const oltSerialCount = {};
  for (const o of onus) {
    const s = (o.serialNumber || o.id || '').toUpperCase();
    if (s) oltSerialCount[s] = (oltSerialCount[s] || 0) + 1;
  }
  // First-seen index per serial (later duplicates are OLT-internal dupes)
  const firstSeenIdx = {};
  onus.forEach((o, i) => {
    const s = (o.serialNumber || o.id || '').toUpperCase();
    if (s && firstSeenIdx[s] === undefined) firstSeenIdx[s] = i;
  });

  const dupCount = onus.filter(o => existingSerials.has((o.serialNumber || o.id || '').toUpperCase())).length;

  // OLT-internal duplicates (later occurrences of same S/N) are always excluded
  const isOltDupRow = (o, i) => {
    const s = (o.serialNumber || o.id || '').toUpperCase();
    return s && firstSeenIdx[s] !== i;
  };

  const handleSkipDups = (checked) => {
    setSkipDups(checked);
    setSelected(prev => {
      const next = new Set(prev);
      onus.forEach((o, i) => {
        if (isOltDupRow(o, i)) { next.delete(i); return; }
        if (checked && existingSerials.has((o.serialNumber || o.id || '').toUpperCase()))
          next.delete(i);
        else if (!checked)
          next.add(i);
      });
      return next;
    });
  };

  const toggleAll = () => {
    const eligible = onus.map((o, i) => ({ o, i })).filter(({ o, i }) => {
      if (isOltDupRow(o, i)) return false;
      if (skipDups && existingSerials.has((o.serialNumber || o.id || '').toUpperCase())) return false;
      return true;
    }).map(({ i }) => i);
    const allSelected = eligible.every(i => selected.has(i));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) eligible.forEach(i => next.delete(i));
      else eligible.forEach(i => next.add(i));
      return next;
    });
  };

  const toggle = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const handleSync = async () => {
    setStep('syncing');
    try {
      const sentSerials = new Set();
      const toSync = onus
        .filter((_, i) => selected.has(i))
        .reduce((acc, o) => {
          const serial = (o.serialNumber || o.id || '').toUpperCase();
          if (!serial || sentSerials.has(serial)) return acc;
          sentSerials.add(serial);
          acc.push(o);
          return acc;
        }, [])
        .map(o => {
          const serial = (o.serialNumber || o.id || '').toUpperCase();
          const map = mappingIndex[serial];
          const custId = map?.customerId?._id || (typeof map?.customerId === 'string' ? map.customerId : null);
          return {
            serialNumber: serial,
            gponId:       o.id || o.onuGponId || '',
            deviceType:   'onu',
            model:        o.model || 'ONU',
            olt: {
              name:    oltCfg?.name || '',
              ip:      oltCfg?.ip   || '',
              ponPort: o.port != null ? String(o.port) : (map?.ponPort != null ? String(map.ponPort) : ''),
            },
            splitter: map ? {
              name:     map.splitterId?.name     || '',
              location: map.splitterId?.location || '',
              port:     map.splitterPort != null ? Number(map.splitterPort) : null,
            } : {},
            ...(custId ? { customer: custId } : {}),
          };
        });

      const { data } = await deviceAPI.bulkSync(toSync);
      setResult(data.data);
      setStep('done');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Sync failed');
      setStep('error');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h3>Import ONU Serials from OLT</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body">
          {step === 'loading' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div className="spinner" style={{ margin: '0 auto 14px' }} />
              <p style={{ color: 'var(--text-muted)', fontSize: '.88rem' }}>Fetching live ONU list from OLT hardware…</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '.78rem' }}>May take 30–60 seconds</p>
            </div>
          )}

          {step === 'error' && (
            <div style={{ padding: 24, background: 'var(--cb-fef2f2)', borderRadius: 8, color: 'var(--c-991b1b)', textAlign: 'center' }}>
              <AlertCircle size={28} style={{ marginBottom: 8 }} />
              <p style={{ margin: 0 }}>{error}</p>
            </div>
          )}

          {step === 'done' && result && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 44, marginBottom: 10 }}>✅</div>
              <h4 style={{ margin: '0 0 6px' }}>Sync Complete</h4>
              <p style={{ color: 'var(--text-muted)', fontSize: '.9rem' }}>
                <strong>{result.created}</strong> new ONU{result.created !== 1 ? 's' : ''} added &nbsp;·&nbsp;
                <strong>{result.updated}</strong> existing updated
              </p>
            </div>
          )}

          {(step === 'preview' || step === 'syncing') && (
            <>
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ fontSize: '.84rem', color: 'var(--text-muted)' }}>
                    {onus.length} ONUs on OLT · <strong>{selected.size}</strong> selected
                  </span>
                  {dupCount > 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', cursor: 'pointer', userSelect: 'none' }}>
                      <input type="checkbox" checked={skipDups} disabled={step === 'syncing'}
                        onChange={e => handleSkipDups(e.target.checked)} />
                      <span>
                        Skip duplicates
                        <span style={{ marginLeft: 5, background: 'var(--cb-fef3c7)', color: 'var(--c-92400e)', borderRadius: 3, padding: '1px 6px', fontSize: '.73rem', fontWeight: 600 }}>
                          {dupCount} already in inventory
                        </span>
                      </span>
                    </label>
                  )}
                </div>
                <button className="btn btn-secondary btn-sm" onClick={toggleAll} disabled={step === 'syncing'}>
                  Select / Deselect All
                </button>
              </div>
              <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--cb-f9fafb)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', width: 36 }}>
                        <input type="checkbox" checked={selected.size === onus.length && onus.length > 0}
                          onChange={toggleAll} disabled={step === 'syncing'} />
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '.78rem' }}>Serial</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '.78rem' }}>Model</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '.78rem' }}>Status</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '.78rem' }}>PON Port</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '.78rem' }}>Mapped To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onus.map((o, i) => {
                      const serial      = (o.serialNumber || o.id || '').toUpperCase();
                      const map         = mappingIndex[serial];
                      const isOnline    = (o.status || '').toLowerCase().includes('online');
                      const isInvDup    = existingSerials.has(serial);      // already in inventory
                      const isOltDup    = serial && firstSeenIdx[serial] !== i; // duplicate within OLT list
                      const isDimmed    = (isInvDup && skipDups) || isOltDup;
                      return (
                        <tr key={i} style={{
                          borderBottom: '1px solid var(--border)',
                          opacity: step === 'syncing' ? 0.6 : (isDimmed ? 0.4 : 1),
                          background: isOltDup ? 'var(--cb-fef2f2)' : isInvDup ? 'var(--cb-fffbeb)' : 'transparent',
                        }}>
                          <td style={{ padding: '7px 12px' }}>
                            <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)}
                              disabled={step === 'syncing' || isOltDup} />
                          </td>
                          <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontWeight: 600, fontSize: '.8rem' }}>
                            {serial || '—'}
                            {isOltDup && (
                              <span style={{ marginLeft: 7, background: 'var(--cb-fee2e2)', color: 'var(--c-991b1b)', borderRadius: 3, padding: '1px 5px', fontSize: '.68rem', fontWeight: 600, verticalAlign: 'middle' }}>
                                OLT duplicate
                              </span>
                            )}
                            {!isOltDup && isInvDup && (
                              <span style={{ marginLeft: 7, background: 'var(--cb-fef3c7)', color: 'var(--c-92400e)', borderRadius: 3, padding: '1px 5px', fontSize: '.68rem', fontWeight: 600, verticalAlign: 'middle' }}>
                                in inventory
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '7px 12px', color: 'var(--text-muted)' }}>{o.model || '—'}</td>
                          <td style={{ padding: '7px 12px' }}>
                            <span style={{
                              background: isOnline ? 'var(--cb-d1fae5)' : 'var(--cb-f3f4f6)',
                              color: isOnline ? 'var(--c-065f46)' : 'var(--c-6b7280)',
                              borderRadius: 4, padding: '1px 7px', fontSize: '.73rem', fontWeight: 600,
                            }}>
                              {o.status || '—'}
                            </span>
                          </td>
                          <td style={{ padding: '7px 12px' }}>
                            {o.port != null ? <span style={{ fontWeight: 600 }}>PON {o.port}</span> : '—'}
                          </td>
                          <td style={{ padding: '7px 12px', fontSize: '.78rem' }}>
                            {map?.customerId?.name ? (
                              <span style={{ color: 'var(--c-1e40af)', fontWeight: 500 }}>{map.customerId.name}</span>
                            ) : map?.splitterId?.name ? (
                              <span style={{ color: 'var(--c-6b7280)' }}>{map.splitterId.name}</span>
                            ) : <span style={{ color: 'var(--text-muted)' }}>Unmapped</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>
          {step === 'preview' && (
            <button className="btn btn-primary" onClick={handleSync} disabled={selected.size === 0}>
              <Download size={14} /> Sync {selected.size} ONU{selected.size !== 1 ? 's' : ''} to Inventory
            </button>
          )}
          {step === 'syncing' && (
            <button className="btn btn-primary" disabled>
              <div className="spinner" style={{ width: 14, height: 14, marginRight: 6 }} /> Syncing…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DevicesPage() {
  useDarkReady();
  const [devices, setDevices]       = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [showSync, setShowSync]     = useState(false);
  const [filters, setFilters]       = useState({ page: 1, limit: 50, status: '', deviceType: '', search: '' });
  const [selected, setSelected]     = useState(new Set());
  const [deleting, setDeleting]     = useState(false);
  const [oltMap, setOltMap]         = useState({});
  const [syncingStatus, setSyncingStatus] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] == null) && delete params[k]);
      const { data } = await deviceAPI.getAll(params);
      setDevices(data.data);
      setPagination(data.pagination || {});
    } catch { toast.error('Failed to load devices'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); setSelected(new Set()); }, [load]);

  // Fetch OLT serial map in background — fills label + live status
  useEffect(() => {
    oltAPI.getSerialMap().then(r => setOltMap(r.data?.data || {})).catch(() => {});
  }, []);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === devices.length) setSelected(new Set());
    else setSelected(new Set(devices.map(d => d._id)));
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} device(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deviceAPI.bulkDelete([...selected]);
      toast.success(`${selected.size} device(s) deleted`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally { setDeleting(false); }
  };

  const handleSyncStatus = async () => {
    setSyncingStatus(true);
    try {
      const { data } = await deviceAPI.syncStatus();
      toast.success(data.message || 'ONU status synced with OLT');
      oltAPI.getSerialMap().then(r => setOltMap(r.data?.data || {})).catch(() => {});
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
    } finally { setSyncingStatus(false); }
  };

  const STATUS_COLOR = {
    in_stock: 'info', installed: 'success', faulty: 'danger', returned: 'warning', retired: 'suspended',
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 pk-head">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Device Inventory</h2>
          <p className="text-muted text-small">{pagination.total || 0} devices · ONU/OLT/Router management</p>
        </div>
        <div className="pk-head-btns" style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={handleSyncStatus} disabled={syncingStatus}
            title="Re-check every ONU against the OLT and correct its In Stock / Installed status">
            <RefreshCw size={14} style={{ marginRight: 6 }} />{syncingStatus ? 'Syncing…' : 'Sync Status from OLT'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowSync(true)}>
            <Download size={14} style={{ marginRight: 6 }} />Import from OLT
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={15} style={{ marginRight: 5 }} />Add Device
          </button>
        </div>
      </div>

      <div className="filter-bar pk-filters dv-filters">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search serial, MAC, model…"
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <select className="form-control" style={{ width: 140 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="in_stock">In Stock</option>
          <option value="installed">Installed</option>
          <option value="faulty">Faulty</option>
          <option value="retired">Retired</option>
        </select>
        <select className="form-control" style={{ width: 140 }} value={filters.deviceType}
          onChange={e => setFilters(f => ({ ...f, deviceType: e.target.value, page: 1 }))}>
          <option value="">All Types</option>
          <option value="onu">ONU</option>
          <option value="olt">OLT</option>
          <option value="router">Router (Broadband)</option>
          <option value="set_top_box">STB (CableTV)</option>
        </select>
        <select className="form-control" style={{ width: 90 }} value={filters.limit}
          onChange={e => setFilters(f => ({ ...f, limit: Number(e.target.value), page: 1 }))}>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={500}>500</option>
          <option value={9999}>All</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div className="pk-bulk" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 8, background: 'var(--cb-fef2f2)', border: '1px solid var(--cb-fecaca)', borderRadius: 8 }}>
          <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--c-991b1b)' }}>{selected.size} device{selected.size !== 1 ? 's' : ''} selected</span>
          <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
            <Trash2 size={13} style={{ marginRight: 5 }} />{deleting ? 'Deleting…' : 'Delete Selected'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())} disabled={deleting}>Clear</button>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : devices.length === 0 ? (
            <div className="empty-state">
              <Server size={32} />
              <h3>No devices found</h3>
              <p>Add devices manually or use <strong>Import from OLT</strong> to sync all ONU serials.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36, padding: '8px 10px' }}>
                    <input type="checkbox"
                      checked={devices.length > 0 && selected.size === devices.length}
                      ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < devices.length; }}
                      onChange={toggleAll} />
                  </th>
                  <th>Serial</th>
                  <th>Type / Model</th>
                  <th>GPON ID</th>
                  <th>Label</th>
                  <th>Status</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {devices.map(d => (
                  <tr key={d._id} style={{ background: selected.has(d._id) ? 'var(--cb-fef2f2)' : undefined }}>
                    <td style={{ padding: '7px 10px' }}>
                      <input type="checkbox" checked={selected.has(d._id)} onChange={() => toggleSelect(d._id)} />
                    </td>
                    <td data-title>
                      {d.deviceType === 'onu' ? (
                        <Link
                          to={`/olt?serial=${encodeURIComponent(d.serialNumber)}`}
                          className="font-mono"
                          style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--primary)', textDecoration: 'none' }}
                          title="View in OLT Management"
                        >
                          {d.serialNumber}
                        </Link>
                      ) : (
                        <div className="font-mono" style={{ fontWeight: 600, fontSize: '.82rem' }}>{d.serialNumber}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: '.83rem', textTransform: 'capitalize' }}>
                        {d.deviceType === 'set_top_box' ? 'STB' : d.deviceType?.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
                        {[d.vendor, d.model].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.8rem' }}>
                      {(oltMap[d.serialNumber]?.gponId || d.gponId)
                        ? <span style={{ fontWeight: 600 }}>{oltMap[d.serialNumber]?.gponId || d.gponId}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td data-keep>
                      {(() => {
                        const oltLabel = oltMap[d.serialNumber]?.label || d.label || '';
                        if (oltLabel) {
                          return (
                            <Link
                              to={`/olt?serial=${encodeURIComponent(d.serialNumber)}`}
                              style={{ fontWeight: 600, fontSize: '.83rem', color: 'var(--primary)', textDecoration: 'none' }}
                              title="View in OLT Management"
                            >
                              {oltLabel}
                            </Link>
                          );
                        }
                        if (d.customer) {
                          return (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '.83rem' }}>{d.customer.name}</div>
                              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{d.customer.customerId}</div>
                            </div>
                          );
                        }
                        return (
                          <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>
                            {d.status === 'in_stock' ? 'Not deployed' : '—'}
                          </span>
                        );
                      })()}
                    </td>
                    <td data-keep>
                      <span className={`badge badge-${STATUS_COLOR[d.status] || 'info'}`} style={{ textTransform: 'capitalize' }}>
                        {d.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                      {d.createdAt ? format(new Date(d.createdAt), 'dd/MM/yy') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {filters.limit < 9999 && pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn"
              disabled={pagination.page <= 1}
              onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>
              {pagination.page} / {pagination.pages}
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>({pagination.total} total)</span>
            </span>
            <button className="page-btn"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      <div className="dv-future" style={{ marginTop: 16, padding: '12px 16px', background: 'var(--bg)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: '.82rem', color: 'var(--text-muted)' }}>
        <strong>Future:</strong> ACS / TR-069 integration will enable remote ONU reboot, WiFi password change, firmware upgrade, and diagnostics from this screen. Schema and routes are already reserved.
      </div>

      {showModal && <AddDeviceModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); load(); }} />}
      {showSync  && <SyncOLTModal  onClose={() => setShowSync(false)}  onSuccess={load} />}
    </div>
  );
}
